import React, { useEffect, useState } from 'react';
import { Share, SquarePlus, X } from 'lucide-react';

// Invite people to add the app to their home screen, but only once they've
// come back (second visit or later) and never inside the installed app. It
// asks at most twice per device, about three months apart, then stays quiet
// for good. Android/Chrome gives us a real install prompt; iOS has none, so
// we show the two-tap recipe instead.

const MIN_VISITS = 2;
const SNOOZE_DAYS = 90;
const MAX_ASKS = 2;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// One visit = one browser session (see App.js splash logic for the same idea)
const countVisit = () => {
  try {
    const count = Number(localStorage.getItem('visitCount') || 0);
    if (sessionStorage.getItem('visitCounted')) return count;
    sessionStorage.setItem('visitCounted', '1');
    localStorage.setItem('visitCount', String(count + 1));
    return count + 1;
  } catch {
    return 0;
  }
};

const snooze = (days) => {
  try {
    localStorage.setItem('installPromptSnoozedUntil', String(Date.now() + days * 86400000));
  } catch {
    // ignore
  }
};

const isSnoozed = () => {
  try {
    return Date.now() < Number(localStorage.getItem('installPromptSnoozedUntil') || 0);
  } catch {
    return false;
  }
};

const dismissCount = () => {
  try {
    return Number(localStorage.getItem('installPromptDismissals') || 0);
  } catch {
    return 0;
  }
};

// Each dismissal counts; after the last allowed one, snooze forever
const recordDismissal = () => {
  const count = dismissCount() + 1;
  try {
    localStorage.setItem('installPromptDismissals', String(count));
  } catch {
    // ignore
  }
  snooze(count >= MAX_ASKS ? 3650 : SNOOZE_DAYS);
};

const InstallPrompt = () => {
  const [installEvent, setInstallEvent] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;
    if (countVisit() < MIN_VISITS || isSnoozed() || dismissCount() >= MAX_ASKS) return undefined;

    if (isIOS()) {
      setVisible(true);
      return undefined;
    }
    // Chrome/Edge/Samsung: the browser tells us when the app is installable
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    recordDismissal();
  };

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    setVisible(false);
    if (outcome === 'accepted') snooze(3650);
    else recordDismissal();
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pointer-events-none animate-slide-up">
      <div
        role="dialog"
        aria-label="Add to Home Screen"
        className="max-w-md mx-auto pointer-events-auto bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-4 flex items-start gap-3"
      >
        <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-xl flex-shrink-0 shadow" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 dark:text-white leading-tight">Add to your Home Screen</p>
          {isIOS() ? (
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 leading-snug">
              Tap <Share size={15} className="inline -mt-0.5 mx-0.5 text-blue-600 dark:text-blue-400" aria-label="Share" />
              then <SquarePlus size={15} className="inline -mt-0.5 mx-0.5" aria-hidden="true" />
              <span className="font-medium">Add to Home Screen</span> for full-screen access, even offline.
            </p>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 leading-snug">
              Opens like an app, works offline, and stays a tap away.
            </p>
          )}
          {!isIOS() && (
            <button
              onClick={install}
              className="mt-3 px-4 py-2 rounded-lg bg-slate-800 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold shadow-sm"
            >
              Install
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          aria-label="Not now"
          className="-mr-1 -mt-1 p-2 rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-white flex-shrink-0"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
